import { Schedule } from '@mui/icons-material';
import { Box, styled } from '@mui/material';
import React from 'react';

import { SLACircleIndicator } from '../../../../../../components/molecules/SLACircleIndicator';
import { COLORS } from '../../../../../../configs/colors';
import { isSafeUrl } from '../../../../../../utils/url-validation';
import { isDateToday } from '../../../MobileServices.utils';
import { SlideProps } from '../SwipeableServiceCard.props';

const CardTop = styled(Box)({
  position: 'relative',
  marginBottom: '4px',
});

const RightColumn = styled(Box)({
  position: 'absolute',
  top: 0,
  right: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: '4px',
});

const JobId = styled(Box)({
  fontWeight: 700,
  fontSize: '0.9rem',
  color: COLORS.NavyBlue,
  letterSpacing: '-0.2px',
  lineHeight: 1.2,
});

const ClientName = styled(Box)({
  fontWeight: 600,
  fontSize: '0.85rem',
  color: COLORS.Black,
  marginBottom: '2px',
  lineHeight: 1.2,
});

const Location = styled(Box)({
  fontSize: '0.78rem',
  color: COLORS.LightGrey,
  marginBottom: '3px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const ServiceType = styled(Box)({
  fontSize: '0.82rem',
  color: COLORS.NavyBlue,
  fontWeight: 700,
  marginBottom: '3px',
  letterSpacing: '-0.1px',
  lineHeight: 1.2,
});

const ScheduledDateBox = styled(Box)({
  fontSize: '0.75rem',
  color: COLORS.NavyBlue,
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: '3px',
  marginBottom: '4px',
  '& svg': {
    fontSize: '12px',
  },
});

const Pills = styled(Box)({
  display: 'flex',
  gap: '4px',
  flexWrap: 'wrap',
  marginBottom: '2px',
});

const PRIORITY_COLOR_MAP: Record<string, string> = {
  P1: COLORS.PriorityP1,
  P2: COLORS.PriorityP2,
  P3: COLORS.PriorityP3,
  P4: COLORS.PriorityP4,
};

const getPriorityColor = (code?: string): string => {
  if (!code) return COLORS.PriorityP3;
  return PRIORITY_COLOR_MAP[code.toUpperCase()] || COLORS.PriorityP3;
};

const Pill = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'pillColor',
})<{ variant: 'priority' | 'brand'; pillColor?: string }>(({ variant, pillColor }) => ({
  fontWeight: 600,
  fontSize: '0.65rem',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  padding: '2px 7px',
  borderRadius: '50px',
  ...(variant === 'priority' && {
    background: pillColor ? `${pillColor}20` : 'rgba(245, 158, 11, 0.1)',
    color: pillColor || COLORS.Orange,
  }),
  ...(variant === 'brand' && {
    background: COLORS.BrandPillBg,
    color: COLORS.DarkGray,
  }),
}));

const CardBottom = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '2px',
});

const TechName = styled(Box)({
  fontSize: '0.75rem',
  color: COLORS.LightGrey,
  flex: 1,
});

const LogoWrapper = styled(Box)({
  width: '24px',
  height: '24px',
  borderRadius: '6px',
  background: `linear-gradient(135deg, ${COLORS.NavyBlue}, ${COLORS.NavyBlueDark})`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: '0.6rem',
  color: COLORS.White,
  flexShrink: 0,
  '& svg': {
    fontSize: '16px',
  },
});

const formatScheduledDate = (dateString: string | undefined): string => {
  if (!dateString) return 'Not scheduled';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Not scheduled';
    if (isDateToday(dateString)) {
      return `Today ${date.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}`;
    }
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return 'Not scheduled';
  }
};

export const SummarySlide: React.FC<SlideProps> = ({ service }) => {
  const clientInitials =
    service.clientHierarchy
      ?.split(' ')
      .filter((w: string) => w.length > 0)
      .map((word: string) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'CL';

  const scheduledToday = isDateToday(service.scheduledDate);

  return (
    <Box data-slide="1">
      <CardTop>
        <JobId sx={{ paddingRight: '70px' }}>#{service.serviceId}</JobId>
        <ClientName sx={{ paddingRight: '70px' }}>
          {service.clientHierarchy || 'Unknown Client'}
        </ClientName>
        <RightColumn>
          <SLACircleIndicator
            clientSLASummary={service.clientSLASummary}
            opsSLASummary={service.opsSLASummary}
          />
          {(service.priority?.shortName || service.priorityDescription) && (
            <Pill
              variant="priority"
              pillColor={service.priority?.color || getPriorityColor(service.priorityCode)}
            >
              {service.priority?.shortName || service.priorityDescription}
            </Pill>
          )}
        </RightColumn>
      </CardTop>
      <Location>{service.locationName || 'Unknown Location'}</Location>
      {service.serviceTypeName && <ServiceType>{service.serviceTypeName}</ServiceType>}
      <ScheduledDateBox
        sx={scheduledToday ? { color: COLORS.LightGreen, fontWeight: 700 } : undefined}
      >
        <Schedule /> {formatScheduledDate(service.scheduledDate)}
      </ScheduledDateBox>
      {service.reportedModel && (
        <Pills>
          <Pill variant="brand">{service.reportedModel}</Pill>
        </Pills>
      )}
      <CardBottom>
        <TechName>{service.primaryAllocation || 'Unallocated'}</TechName>
        <LogoWrapper>
          {service.logoPic && isSafeUrl(service.logoPic) ? (
            <img
              src={service.logoPic}
              alt={service.clientHierarchy || 'Client'}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          ) : (
            clientInitials
          )}
        </LogoWrapper>
      </CardBottom>
    </Box>
  );
};
